<template>
  <div>
    <b-container v-if="loading">
      <b-row>
        <b-col class="text-center" align-self="center" >
          <b-spinner variant="primary" class="m-5" label="Loading"></b-spinner>
        </b-col>
      </b-row>
    </b-container>
    <b-container v-else>
      <h3 class="my-4" v-html="$t('project.projects')"></h3>
      <b-row class="mb-3 mt-3">
        <b-col class="text-left" cols="8">
          <b-input-group class="searchfield-group">
            <span>
              <b-icon icon="search" />
            </span>
            <b-form-input
              v-model="filter"
              debounce="400"
              class="searchfield"
              :placeholder="$t('search')"
            ></b-form-input>
          </b-input-group>
        </b-col>
        <b-col class="text-right">
          <router-link to="/#new">
            <b-button variant="primary">{{
              $t("project.createNewProject")
            }}</b-button>
          </router-link>
        </b-col>
      </b-row>
    </b-container>

    <b-row
      v-if="$store.state.projects.projectList.length == 0"
      class="mt-5"
    >
      <b-col class="mt-5" style="text-align: center">
        <h3>{{ $t("project.noProject") }}</h3>
        <h6 class="mt-4">{{ $t("project.startWithConfiguration") }}</h6>
        <router-link to="/#new">
          <b-button class="mt-2" variant="primary">{{
            $t("project.createNewProject")
          }}</b-button>
        </router-link>
      </b-col>
    </b-row>

    <b-container v-if="$store.state.projects.projectList.length != 0">
      <b-table
        striped
        hover
        no-local-sorting
        :items="$store.state.projects.projectList"
        :fields="fields"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        @sort-changed="sortFn"
        class="project-table"
      >
        <template v-slot:cell(number)="{ item, value }">
          <router-link :to="`/projects/${item.number}`">{{
            value
          }}</router-link>
        </template>
        <template v-slot:cell(name)="data">
          <span class="name" v-b-popover.hover="data.value">{{
            data.value
          }}</span>
        </template>
        <template v-slot:cell(description)="data">
          <span class="description" v-b-popover.hover="data.value">{{
            data.value
          }}</span>
        </template>
        <template v-slot:cell(status)="data">{{
          $t(`project.status.${data.value}`)
        }}</template>
        <template v-slot:cell(btnCol)="data">
          <b-link
            @click="onDeleteClick(data.item.number)"
            :disabled="
              data.item.status == 'ordered' || data.item.status == 'requested'
            "
            :style="{
              cursor:
                data.item.status == 'ordered' || data.item.status == 'requested'
                  ? 'not-allowed'
                  : 'pointer',
            }"
          >
            <font-awesome-icon
              icon="trash-alt"
              :style="{
                color:
                  data.item.status == 'ordered' ||
                  data.item.status == 'requested'
                    ? 'rgba(100,100,100,0.3)'
                    : 'inherit',
              }"
            />
          </b-link>
        </template>
      </b-table>
      <b-pagination
        align="center"
        :total-rows="$store.state.projects.projectCount"
        v-model="currentPage"
        :per-page="perPage"
        @input="getAllProjects(currentPage)"
      />
    </b-container>
    <ProjectCreateDialog
      :show="$route.hash == '#new'"
      @cancel="$router.replace('/')"
      @submit="onProjectCreated"
    />
    <ProjectDeleteDialog
      ref="deleteProjectDialog"
      :projectNumber="projectToDelete"
      @confirm="deleteProject"
    />
  </div>
</template>

<script>
import ProjectCreateDialog from "./ProjectCreateDialog";
import ProjectDeleteDialog from "../components/ProjectDeleteDialog";

export default {
  data() {
    return {
      show: true,
      projectToDelete: null,
      filter: null,
      currentPage: 1,
      perPage: 10,
      sortBy: "-number",
      mySortBy: "-number",
      loading: false
    };
  },
  computed: {
    fields() {
      return [
        {
          key: "number",
          label: "ID",
          sortable: true,
          class: "text-right",
          thStyle: { width: "6%" }
        },
        {
          key: "name",
          label: this.$t("project.name"),
          sortable: true,
          class: "text-left",
          thStyle: { width: "20%" }
        },
        {
          key: "description",
          label: this.$t("project.description"),
          class: "text-left",
          thStyle: { width: "25%" }
        },
        {
          key: "status",
          label: this.$t("project.status.name"),
          class: "text-center",
          thStyle: { width: "15%" }
        },
        {
          key: "owner",
          label: this.$t("user"),
          sortable: true,
          class: "text-left",
          thStyle: { width: "10%" }
        },
        {
          key: "created",
          label: this.$t("project.createdOn"),
          type: "date",
          formatter: dateString => {
            return new Date(dateString).toLocaleString(this.$i18n.locale, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "numeric",
              minute: "numeric",
              second: "numeric"
            });
          },
          sortable: "true",
          class: "text-left",
          thStyle: { width: "25%" }
        },
        {
          key: "btnCol",
          label: "",
          thStyle: { width: "4%" }
        }
      ];
    }
  },
  components: { ProjectCreateDialog, ProjectDeleteDialog },
  async created() {
    try {
      this.loading = true;
      await this.getAllProjects();
    } finally {
      this.loading = false;
    }
  },
  methods: {
    sortFn(sort) {
      if (sort.sortDesc) {
        this.mySortBy = "-" + sort.sortBy;
      } else {
        this.mySortBy = sort.sortBy;
      }
      this.currentPage = 1;
      this.getAllProjects();
    },
    onProjectCreated: async function(project) {
      const id = await this.$store.dispatch("configurations/createNewConfiguration");
      this.$router.push({
        name: "columnTypesWithConfig",
        params: {
          projectId: project.number,
          configurationId: id
        }
      });
    },
    getAllProjects: async function() {
      var filter = this.filter == null ? "" : this.filter.trim();
      var offset = this.perPage * this.currentPage - this.perPage;
      this.$store.commit("projects/setProjectFilter", {
        query: filter,
        sortBy: this.mySortBy,
        limit: this.perPage,
        offset
      })
      await this.$store.dispatch("projects/loadProjectList");
    },
    onDeleteClick(id) {
      this.projectToDelete = id;
      this.$refs.deleteProjectDialog.show();
    },
    deleteProject: async function() {
      await this.$store.dispatch("projects/deleteProject", this.projectToDelete);
      this.getAllProjects();
    }
  },
  watch: {
    filter: function() {
      this.currentPage = 1
      this.getAllProjects();
    }
  }
};
</script>

<style lang="scss">
@import "../styles/common";

.description {
  max-width: 30em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.name {
  max-width: 20em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.go-to-project-link {
  color: $eepos-blue !important;
  cursor: pointer;
}

.searchfield-group {
  position: relative;
  z-index: 99;
  & .form-control {
    padding: 10px 10px 10px 35px;
    background: white;
  }
  & span {
    position: absolute;
    pointer-events: none;
    left: 11px;
    top: 8px;
    z-index: 9;
  }
  input {
    font-weight: 300;
    font-size: 1.1em;
  }
}
</style>
